import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable } from 'rxjs/internal/Observable';
import { UtilService } from './util.service';
import { formatDate } from 'date-fns';
import {
  IGeneratedTask,
  IGeneratedTasksModel,
  ILoanBorrower,
  ITaskCommentPost,
  ITaskRequest,
} from '../../modules/tasks/model/types';
import { environment } from '../../../environments/environment';
import { HttpParams } from '@angular/common/http';
import { BehaviorSubject, map, tap } from 'rxjs';
import notify from 'devextreme/ui/notify';
import { IAudiLog } from '../components/task-history/task-history.models';
import { IUserNotification } from '../stores';
import { IFormMetadata, IFormSummary, IFormTemplate, IFormValidationRule } from '../../framework/models/form-template';

@Injectable({
  providedIn: 'root',
})
export class TaskDataService {
  constructor(
    private readonly api: ApiService,
    private readonly utilService: UtilService
  ) { }

  private readonly baseUrl: string = environment.taskManagerServiceEndPoint;
  private readonly controller = 'Task';
  private readonly dashboardController = 'TaskDashboard';
  private readonly generatedTasksController = 'GeneratedTasks';
  private readonly staticDataController = 'StaticData';
  private readonly taskCommentsController = 'TaskComments';
  private readonly taskTemplateController = 'TaskTemplates';
  private readonly taskHistoryController = 'TaskHistory';
  private readonly formTemplateController = 'FormTemplates';
  private readonly getMethod = 'GetDx';
  private readonly dateFormat: string = 'yyyy/MM/dd';
  private readonly statusFlags: string =
    '&IncludeOverdueStatus=true&IncludeAlertStatus=true&IncludeBacklogStatus=true&IncludeHighRisk=true&IncludeMediumRisk=true&IncludeLowRisk=true&IncludeOtherRisk=true';

  // Statuses observables with caching
  private statusesCache = new BehaviorSubject<any[]>([]);
  private statusesLoaded = false;
  private statusesLoading = false;

  statuses$ = this.statusesCache.asObservable();

  // Helper method to extract task ID from response
  private getTaskId(task: any): string | number {
    return task?.taskId ?? task?.id ?? 'unknown';
  }

  // Method to trigger statuses retrieval with caching
  public retrieveStatuses(): void {
    // Only retrieve if not already loaded or loading
    if (!this.statusesLoaded && !this.statusesLoading) {
      this.statusesLoading = true;
      this.getStatuses().subscribe({
        next: (result) => {
          if (result && result[1]) {
            this.statusesCache.next(result[1]);
            this.statusesLoaded = true;
          }
          this.statusesLoading = false;
        },
        error: (error) => {
          console.error('Error loading statuses:', error);
          this.statusesLoading = false;
        }
      });
    }
  }

  // Method to clear statuses cache (if needed for refresh)
  public clearStatusesCache(): void {
    this.statusesLoaded = false;
    this.statusesLoading = false;
    this.statusesCache.next([]);
  }

  // Method to get hold status names
  public getHoldStatusNames(): Observable<string[]> {
    return this.statuses$.pipe(
      map(result => {
        if (result && Array.isArray(result)) {
          return result.filter((status: any) => status.isBlocked === true)
            .map((status: any) => status.name);
        }
        return [];
      })
    );
  }

  public getLoanBorrowers(): Observable<ILoanBorrower[]> {
    return this.api.get<ILoanBorrower[]>(`${this.baseUrl}/${this.staticDataController}/LoanBorrowers`);
  }

  public getStaticData(staticDataType: string): Observable<any> {
    return this.api.get(`${this.baseUrl}/${this.staticDataController}?type=${staticDataType}`);
  }

  public getCurrencies(): Observable<any> {
    return this.api.get(`${this.baseUrl}/${this.staticDataController}/Currencies`);
  }

  public getStatuses(): Observable<any> {
    return this.api.get(`${this.baseUrl}/${this.staticDataController}/Statuses`);
  }

  public getFeeTypes(): Observable<any> {
    return this.api.get(`${this.baseUrl}/${this.staticDataController}/FeeTypes`);
  }

  public getLendersForLoanNumber(loanNumber: string): Observable<any> {
    return this.api.get(`${this.baseUrl}/StaticData/Lenders?loanNumber=${loanNumber}`);
  }

  public getReserveTypes(staticDataType: string): Observable<any> {
    return this.api.get(`${this.baseUrl}/StaticData/ReserveTypes`);
  }
  public getTaskTemplates(isWorkflow: boolean): Observable<any> {
    return this.api.get(
      `${this.baseUrl}/${this.taskTemplateController}?isActive=true&isWorkflow=${isWorkflow}`
    );
  }

  public getDefaultTasksForTemplate(templateId: string): Observable<any> {
    return this.api.get(
      `${this.baseUrl
      }/${this.taskTemplateController}/DefaultTasks?TemplateId=${templateId}&PageNumber=${1}&PageSize=${100}`
    );
  }

  public assignTaskToUser(userId: string, taskIds: string[]): Observable<any> {
    return this.api.put(`${this.baseUrl}/${this.controller}/AssignToUser`, [
      { userId, taskIds },
    ]);
  }

  public assignTaskToMe(taskIds: string[]): Observable<any> {
    return this.api.put(
      `${this.baseUrl}/${this.controller}/AssignToMe`,
      taskIds
    );
  }

  public getAll(pageStart: number, pageSize: number): Observable<any> {
    return this.api.get(
      `${this.baseUrl}/${this.controller}/${this.getMethod}?IsActive=true&PageNumber=${pageStart}&PageSize=${pageSize}${this.statusFlags}`
    );
  }

  public getAllWithParameters(params: HttpParams): Observable<any> {
    return this.api.getWithParameters(
      `${this.baseUrl}/${this.controller}/${this.getMethod}?IsActive=true`,
      params
    );
  }

  public getAllForUser(pageStart: number, pageSize: number, userId: string) {
    return this.api.post(
      `${this.baseUrl}/${this.controller}/${this.getMethod}?IsActive=true&PageNumber=${pageStart}&PageSize=${pageSize}${this.statusFlags}&UserId=${userId}`,
      '[{ "columnName":"dueDate", "byDescending": "true" }]'
    );
  }

  public getDashboardData(params: HttpParams): Observable<any> {
    return this.api.getWithParameters(
      `${this.baseUrl}/${this.dashboardController}/${this.getMethod}?IsActive=true`,
      params
    );
  }

  public getAllForStatusInDateRange(
    pageStart: number,
    pageSize: number,
    startDate: Date,
    endDate: Date,
    status: string
  ) {
    let filter: string = `DueDateRangeFilter.From=${formatDate(
      startDate,
      this.dateFormat
    )}&DueDateRangeFilter.To=${formatDate(endDate, this.dateFormat)}`;
    return this.api.post(
      `${this.baseUrl}/${this.controller}/${this.getMethod}?IsActive=true&PageNumber=${pageStart}&PageSize=${pageSize}&${filter}${this.statusFlags}`,
      '[{ "columnName":"dueDate", "byDescending": "true" }]'
    );
  }

  public getAllInDateRange(
    pageStart: number,
    pageSize: number,
    startDate: Date,
    endDate: Date
  ) {
    let filter: string = `DueDateRangeFilter.From=${formatDate(
      startDate,
      this.dateFormat
    )}&DueDateRangeFilter.To=${formatDate(endDate, this.dateFormat)}`;
    return this.api.post(
      `${this.baseUrl}/${this.controller}/${this.getMethod}?IsActive=true&PageNumber=${pageStart}&PageSize=${pageSize}&${filter}${this.statusFlags}`,
      '[{ "columnName":"dueDate", "byDescending": "true" }]'
    );
  }

  public postComment(taskComment: ITaskCommentPost): Observable<any> {
    return this.api.post(`${this.baseUrl}/${this.taskCommentsController}`, [
      taskComment,
    ]);
  }

  public getTaskComments(taskId: string): Observable<any> {
    return this.api.get(
      `${this.baseUrl}/${this.taskCommentsController}?taskId=${taskId}`
    );
  }

  public markAllCommentsAsReadForTask(taskId: string): Observable<any> {
    return this.api.put(
      `${this.baseUrl}/${this.taskCommentsController}/MarkAllAsReadForTask?taskId=${taskId}`,
      null
    );
  }

  public markAllUrlsAsReadForTask(taskId: string): Observable<any> {
    return this.api.put(
      `${this.baseUrl}/TaskUrls/MarkAllAsReadForTask?taskId=${taskId}`,
      null
    );
  }

  public getGeneratedTasks(model: IGeneratedTasksModel[]): Observable<any> {
    return this.api.post(`${this.baseUrl}/${this.generatedTasksController}/GetTasks`, model);
  }

  public postGeneratedTasks(tasks: IGeneratedTask[]): Observable<any> {
    return this.api.post(`${this.baseUrl}/${this.generatedTasksController}/PostTasks`, tasks);
  }

  public getGenerateTaskMetadata(id: string): Observable<any> {
    return this.api.get(`${this.baseUrl}/${this.generatedTasksController}/metadata/${id}`);
  }

  public postGenerateTaskMetadata(metadata: any): Observable<any> {
    return this.api.post(`${this.baseUrl}/${this.generatedTasksController}/metadata`, metadata);
  }

  public updateTask(task: ITaskRequest): Observable<any> {
    return this.api.put(`${this.baseUrl}/${this.controller}`, [task]);
  }

  public updateTasks(tasks: ITaskRequest[]): Observable<any> {
    return this.api.put(`${this.baseUrl}/${this.controller}`, tasks).pipe(
      tap(response => {
        const id = this.getTaskId(response);
        notify(
          this.utilService.buildTaskUpdatedMessage(id),
          'success',
          this.utilService.getNotificationTimeout()
        );
      })
    );
  }

  public insertTask(task: ITaskRequest): Observable<any> {
    return this.api.post(`${this.baseUrl}/${this.controller}`, task).pipe(
      tap(response => {
        const id = this.getTaskId(response);
        notify(
          this.utilService.buildTaskCreatedMessage(id),
          'success',
          this.utilService.getNotificationTimeout()
        );
      })
    );
  }

  public insertTaskAsFormData(formData: FormData): Observable<any> {
    return this.api.postMultiPartFormData(
      `${this.baseUrl}/${this.controller}`,
      formData
    ).pipe(
      tap(response => {
        const id = this.getTaskId(response);
        notify(
          this.utilService.buildTaskCreatedMessage(id),
          'success',
          this.utilService.getNotificationTimeout()
        );
      })
    );
  }

  public updateTaskAsFormData(formData: FormData): Observable<any> {
    return this.api.putMultiPartFormData(
      `${this.baseUrl}/${this.controller}/Form`,
      formData
    ).pipe(
      tap(response => {
        const id = this.getTaskId(response);
        notify(
          this.utilService.buildTaskUpdatedMessage(id),
          'success',
          this.utilService.getNotificationTimeout()
        );
      })
    );
  }

  public getTaskById(taskId: string): Observable<any> {
    return this.api.get(
      `${this.baseUrl}/${this.controller}/GetById?taskId=${taskId}`
    );
  }

  public getMultipleTasksById(
    loadOptions: any,
    taskIds: string[]
  ): Observable<any> {
    let options = {
      ...loadOptions,
      taskIds: taskIds,
    };

    // console.log('options', options);
    return this.api.post(
      `${this.baseUrl}/${this.controller}/GetMultipleByIdDx`,
      options
    );
  }

  public getClients(): Observable<any> {
    return this.api.get(`${this.baseUrl}/${this.staticDataController}/Clients`);
  }

  public getNotifications(): Observable<IUserNotification[]> {
    return this.api.fetch<IUserNotification[]>(`${this.baseUrl}/Notifications`)
  }

  public setNotificationAsRead(notificationIds: string[]): Observable<any> {
    return this.api.put(`${this.baseUrl}/Notifications/MarkAsRead`, notificationIds);
  }

  getLoanNumbersWithDeal(): Observable<any> {
    return this.api.get(`${this.baseUrl}/${this.staticDataController}/LoanNumbersWithDeals`);
  }

  getLoanNumbersWithDealAndBorrowers(): Observable<any> {
    return this.api.get(`${this.baseUrl}/${this.staticDataController}/LoanNumbersDealBorrowers`);
  }

  getDeals(): Observable<any> {
    return this.api.get(`${this.baseUrl}/${this.staticDataController}/Deals`);
  }

  getLoanNumbers(): Observable<any> {
    return this.api.get(`${this.baseUrl}/${this.staticDataController}/LoanNumbers`);
  }

  getLoanDetail(facilityId: string): Observable<any> {
    return this.api.get(`${this.baseUrl}/${this.staticDataController}/LoanNumbersWithDealsByLoanNumber?loanNumber=${facilityId}`);
  }

  public getTaskTemplateType(): Observable<any> {
    return this.api.get(`${this.baseUrl}/TaskTemplates/Types`);
  }

  public getTaskTemplatesByType(templateType: string): Observable<any> {
    return this.api.get(
      `${this.baseUrl}/${this.taskTemplateController}/ByType?templateType=${templateType}&isActive=true`
    );
  }

  public getTaskHistory(taskId: string): Observable<IAudiLog[]> {
    return this.api.fetch<IAudiLog[]>(`${this.baseUrl}/${this.taskHistoryController}?taskId=${taskId}`);
  }

  public getFormTemplates(): Observable<IFormSummary[]> {
    return this.api.fetch<IFormSummary[]>(`${this.baseUrl}/${this.formTemplateController}`);
  }

  public getFormTemplate(formType: string, version?: string, locale?: string): Observable<IFormTemplate> {
    let params = new HttpParams();

    if (version) {
      params = params.set('version', version);
    }
    if (locale) {
      params = params.set('locale', locale);
    }

    return this.api.fetch<IFormTemplate>(
      `${this.baseUrl}/${this.formTemplateController}/template/${formType}`,
      { params }
    ).pipe(map(template => {
      const parseJsonSafe = (value: unknown): unknown => {
        if (typeof value !== 'string') {
          return value;
        }

        try {
          return JSON.parse(value);
        } catch {
          // If parsing fails, fall back to the original value to avoid breaking form rendering
          return value;
        }
      };

      template.sections.forEach(section => {
        if (section && section.metadata !== undefined && section.metadata !== null) {
          section.metadata = parseJsonSafe(section.metadata);
        }
      });

      template.fields.forEach(field => {
        if (!field) {
          return;
        }

        if (field.metadata !== undefined && field.metadata !== null) {
          field.metadata = parseJsonSafe(field.metadata);
        }

        if (field.validationRules !== undefined && field.validationRules !== null) {
          field.validationRules = parseJsonSafe(field.validationRules as unknown as string) as IFormValidationRule[];
        }

        if (field.displayConditions !== undefined && field.displayConditions !== null) {
          field.displayConditions = parseJsonSafe(field.displayConditions);
        }
      });

      return template;
    }));
  }
}
